import React from 'react';
import classNames from 'classnames';
import { LocationType } from '../../../constants';
import CCServiceTypeAhead from './CCServiceTypeAhead';
import {
  healthServices,
  urgentCareServices,
  emergencyCareServices,
} from '../../../config';
import { ServiceTypeInputTypes } from '../../../types';
import ServicesLoadingOrShow from './ServicesLoadingOrShow';
import VAMCServiceAutosuggest from './VAMCServiceAutosuggest';

const ServiceType = ({
  currentQuery,
  getProviderSpecialties,
  handleServiceTypeChange,
  isMobile,
  isSmallDesktop,
  isTablet,
  committedVamcServiceDisplay,
  onVamcDraftChange,
  searchInitiated,
  setSearchInitiated,
  vamcAutoSuggestEnabled,
}) => {
  const {
    facilityType,
    serviceType,
    serviceTypeChanged,
    submitErrorServiceType,
  } = currentQuery;

  const isHealthFacility = facilityType === LocationType.HEALTH;
  const serviceNotInStaticList = serviceType && !healthServices[serviceType];
  const shouldUseVamcAutosuggest =
    isHealthFacility && (vamcAutoSuggestEnabled || serviceNotInStaticList);

  if (shouldUseVamcAutosuggest) {
    return (
      <VAMCServiceAutosuggest
        // Key forces complete remount when facility type changes, ensuring fresh internal state
        key={`vamc-autosuggest-${facilityType}`}
        committedServiceDisplay={committedVamcServiceDisplay}
        isMobile={isMobile}
        onDraftChange={onVamcDraftChange}
        searchInitiated={searchInitiated}
        setSearchInitiated={setSearchInitiated}
      />
    );
  }

  const disabled = ![
    LocationType.HEALTH,
    LocationType.URGENT_CARE,
    LocationType.CC_PROVIDER,
    LocationType.EMERGENCY_CARE,
  ].includes(facilityType);

  const showError =
    submitErrorServiceType || (serviceTypeChanged && !disabled && !serviceType);
  const filteredHealthServices = healthServices;

  let services;
  let serviceTypeLabel = 'Select a health service';

  // Determine what service types to display for the location type (if any).
  switch (facilityType) {
    case LocationType.HEALTH:
      services = filteredHealthServices;
      break;
    case LocationType.URGENT_CARE:
      services = urgentCareServices;
      serviceTypeLabel = 'Select an urgent care network';
      break;
    case LocationType.EMERGENCY_CARE:
      services = emergencyCareServices;
      serviceTypeLabel = 'Select an emergency care network';

      break;
    case LocationType.BENEFITS:
      return null;
    case LocationType.CC_PROVIDER:
      return (
        <ServicesLoadingOrShow
          serviceType="ppms_services"
          getProviderSpecialties={getProviderSpecialties}
        >
          <div
            id="service-typeahead-container"
            className={isMobile ? 'typeahead-mobile' : 'typeahead-tablet'}
            data-testid="cc-service-typeahead-pd"
          >
            <CCServiceTypeAhead
              handleServiceTypeChange={handleServiceTypeChange}
              initialSelectedServiceType={serviceType}
              isSmallDesktop={isSmallDesktop}
              showError={showError}
            />
          </div>
        </ServicesLoadingOrShow>
      );
    default:
      return null; // do not show a disabled dropdown for progressive disclosure
  }

  // Create option elements for each VA service type.
  const options = Object.keys(services).map(service => (
    <option key={service} value={service} style={{ fontWeight: 'bold' }}>
      {services[service]}
    </option>
  ));

  return (
    <div
      className={classNames({
        'service-type-dropdown-mobile': isMobile,
        'service-type-dropdown-tablet': isTablet,
        'service-type-dropdown-desktop': isSmallDesktop,
      })}
      data-testid={disabled ? 'disabled-service-type-dropdown' : 'service-type'}
    >
      <label htmlFor="service-type-dropdown">{serviceTypeLabel}</label>
      <select
        id="service-type-dropdown"
        disabled={disabled || !facilityType}
        value={serviceType || ''}
        onChange={handleServiceTypeChange}
      >
        {options}
      </select>
    </div>
  );
};

ServiceType.propTypes = ServiceTypeInputTypes;

export default ServiceType;
